import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import Input from "./Input";
import Select from "./Select";
import {
  searchContact,
  getUserProgramUnits,
  getClientCompanies,
  getClientDiagnosisDetails,
} from "../services/soar";
import {
  getFieldDate,
  getFieldName,
  getFieldText,
  createEntity,
  updateEntity,
  checkKeyPress,
} from "./../../scripts/helperScripts";
import contactOptionSets from "../../options/contact.json";

function ClientIntakeModal({
  isOpen,
  nextModal,
  isLastModal,
  setClientId,
  setClientProgram,
  setFosterHomeProgram,
  closeUpdateRecordsModal,
}) {
  const [data, setData] = useState({ addNewClient: false });
  const [loadStatus, setLoadStatus] = useState({});
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientCompanies, setClientCompanies] = useState([]);
  const [addNewClient, setAddNewClient] = useState(false);
  const [programUnits, setProgramUnits] = useState([]);
  const [programUnitOptions, setProgramUnitOptions] = useState([]);
  const [selectedProgramUnitCompany, setSelectedProgramUnitCompany] = useState(
    {}
  );
  const [addFosterHome, setAddFosterHome] = useState(false);
  const [fosterHomeUnit, setFosterHomeUnit] = useState({});
  const [bedDiagnosis, setBedDiagnosis] = useState();
  const [errors, setErrors] = useState({});
  const [controllerSignal, setControllerSignal] = useState({});
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    client: Joi.string()
      .allow("")
      .label("Client")
      .when("addNewClient", { is: false, then: Joi.string().required() }),
    dateOfAdmission: Joi.date()
      .required()
      .max(moment().add(5, "days").toDate())
      .min(new Date("1/1/1900"))
      .label("Date of Admission")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date of Admission cannot be future dated beyond today +5 days`;
              break;
            case "date.min":
              error.message = `Date of Admission cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    firstName: Joi.string().allow("").label("First Name").when("addNewClient", {
      is: true,
      then: Joi.string().required(),
    }),
    lastName: Joi.string().allow("").label("Last Name").when("addNewClient", {
      is: true,
      then: Joi.string().required(),
    }),
    dateOfBirth: Joi.date()
      .label("Date of Birth")
      .max("now")
      .min(new Date("1/1/1900"))
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date of Birth cannot be future dated`;
              break;
            case "date.min":
              error.message = `Date of Birth cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      })
      .allow(""),
    sex: Joi.string().label("Sex").allow(""),
    ssn: Joi.string()
      .label("SSN")
      .regex(
        /^(?!(000|666|9))\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/,
        "SSN (xxx-xx-xxxx)"
      )
      .max(11)
      .allow(""),
    programUnit: Joi.string().required().label("Program Unit"),
    addNewClient: Joi.boolean(),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    const controller = new AbortController();
    setControllerSignal(controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  const safyUser = useMemo(() => {
    return safyInfo?.user;
  }, [safyInfo]);

  const userBusinessUnit = useMemo(() => {
    return safyUser?.safyInfo?.BusinessUnitIdName;
  }, [safyUser]);

  const userDivisions = useMemo(() => {
    return safyUser?.divisions || [];
  }, [safyUser]);

  // const userModules = useMemo(() => {
  //   return safyUser?.modules;
  // }, [safyUser]);

  // const userIsAdmin = useMemo(() => {
  //   return userModules?.includes("Admin");
  // }, [userModules]);

  useEffect(() => {
    if (
      loadStatus.programsLoaded ||
      loadStatus.programsLoading ||
      !userBusinessUnit
    ) {
      return;
    }

    const loadProgramUnits = async () => {
      setLoadStatus((ls) => {
        return { ...ls, programsLoading: true, programsLoaded: false };
      });
      const response = await getUserProgramUnits(
        userBusinessUnit,
        controllerSignal
      );

      if (!response) {
        return;
      }

      const options = response
        .filter((programUnit) => {
          if (
            programUnit.ccx_expdate !== undefined ||
            getFieldName(programUnit.ccx_programtype) === "Foster Home"
          ) {
            return false;
          }

          let userHasDivision = false;
          const facility = getFieldName(programUnit.ccx_facility);
          userDivisions.forEach((division) => {
            if (division.toLowerCase() === facility.toLowerCase()) {
              userHasDivision = true;
            }
          });
          return userHasDivision;
        })
        .map((programUnit) => {
          return {
            _id: programUnit.ccx_unitid,
            name: programUnit.ccx_name,
          };
        });

      setProgramUnitOptions(options);
      setProgramUnits(response);
      setLoadStatus((ls) => {
        return { ...ls, programsLoading: false, programsLoaded: true };
      });
    };

    loadProgramUnits();
  }, [
    loadStatus.programsLoaded,
    loadStatus.programsLoading,
    userBusinessUnit,
    userDivisions,
    controllerSignal,
  ]);

  const getGender = () => {
    const gendercode = contactOptionSets.gendercode.filter(
      (code) => code._id === data.sex
    )[0];
    if (gendercode && gendercode.name) return gendercode.name;
    else return "";
  };

  const getProgramUnitName = () => {
    const programUnit = programUnitOptions.filter(
      (unit) => unit._id === data.programUnit
    )[0];
    if (programUnit) return programUnit.name;
  };

  const handleClientSearch = async () => {
    setLoadStatus((ls) => {
      return { ...ls, loadingContacts: true, contactsLoaded: false };
    });
    const response = await searchContact(
      clientSearchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName,
      controllerSignal
    );

    if (controllerSignal.aborted) {
      return;
    }

    if (response) {
      const options = response
        .map((contact) => {
          return {
            _id: contact.contactid,
            name: `${contact.fullname}${
              contact.birthdate
                ? " - DOB: " + getFieldDate(contact.birthdate)
                : ""
            }`,
            dob: contact.birthdate,
            contactType: getFieldName(contact.ccx_contacttype),
          };
        })
        .sort((a, b) => a.name - b.name);
      setClientOptions(options);
    }

    setLoadStatus((ls) => {
      return { ...ls, loadingContacts: false, contactsLoaded: true };
    });
  };

  const checkErrors = (input) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }

    setErrors(errs);
  };

  const handleChange = ({ currentTarget: input }) => {
    checkErrors(input);
    setData((d) => {
      const obj = { ...d };
      obj[input.name] = input.value;
      return obj;
    });
  };

  const handleClientChange = ({ currentTarget: input }) => {
    checkErrors(input);
    setData((d) => {
      const obj = { ...d };
      obj[input.name] = input.value;
      return obj;
    });

    if (!input.value) {
      return;
    }

    const client = input.value;

    const loadClientCompanies = async (count = 1) => {
      const response = await getClientCompanies(
        client,
        userBusinessUnit,
        controllerSignal
      );

      if (controllerSignal.aborted) {
        return;
      }

      if (!response) {
        loadClientCompanies(count + 1);
        return;
      }

      const companies = response.filter(
        (company) =>
          company.ccx_expdate === undefined &&
          company.ccx_dischargedate === undefined &&
          getFieldText(company.statecode) === "0"
      );
      setClientCompanies(companies);
    };

    const loadClientDiagnosisDetails = async (count = 1) => {
      const response = await getClientDiagnosisDetails(
        client,
        controllerSignal
      );

      if (controllerSignal.aborted) {
        return;
      }

      if (!response) {
        loadClientDiagnosisDetails(count + 1);
        return;
      }

      const bedDiagnosis = response.filter(
        (dxDetails) =>
          dxDetails.ccx_expdate === undefined &&
          dxDetails["dx.ccx_expdate"] === undefined &&
          getFieldName(dxDetails.ccx_clientdiagnosis) === "Bed Diagnosis"
      )[0];

      setBedDiagnosis(bedDiagnosis);
    };

    loadClientCompanies();
    loadClientDiagnosisDetails();
  };

  const handleProgramUnitChange = ({ currentTarget: input }) => {
    checkErrors(input);
    setData((d) => {
      const obj = { ...d };
      obj[input.name] = input.value;
      return obj;
    });

    if (!input.value) {
      return;
    }

    const selectedUnit = programUnits.filter(
      (unit) => unit.ccx_unitid === input.value
    )[0];
    const programUnitType = getFieldName(selectedUnit.ccx_programtype);

    if (programUnitType === "TFC") {
      const fosterHome = programUnits.filter(
        (unit) =>
          getFieldText(unit.statecode) === "0" &&
          getFieldText(unit.ccx_facility) ===
            getFieldText(selectedUnit.ccx_facility) &&
          getFieldName(unit.ccx_programtype) === "Foster Home"
      )[0];
      setFosterHomeUnit(fosterHome || {});
      setAddFosterHome(true);
    } else {
      setFosterHomeUnit({});
      setAddFosterHome(false);
    }

    const programUnitCompany = {
      companyId: getFieldText(selectedUnit["facility.ccx_agency"]),
      companyName: getFieldName(selectedUnit["facility.ccx_agency"]),
    };

    setSelectedProgramUnitCompany(programUnitCompany);
  };

  const handleSSNChange = ({ currentTarget: input }) => {
    checkErrors(input);
    setData((d) => {
      const obj = { ...d };
      let numericSSN = input.value;
      numericSSN = numericSSN.replace(/\D/g, "");
      let value = "";

      [...numericSSN].forEach((digit, index) => {
        if (index === 3 || index === 5) {
          value += "-";
        }

        value += digit;
      });

      obj.ssn = value;
      return obj;
    });
  };

  const validateProperty = ({ name, value }) => {
    if (name !== "ssn") {
      const obj = { [name]: value };
      const schema = { [name]: joiSchema[name] };
      const { error } = Joi.validate(obj, schema);
      return error ? error.details[0].message : null;
    } else return null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = () => {
    const clientCompanyObj = {
      ccx_company: {
        type: "entity",
        value: {
          entityName: "ccx_agency",
          entityId: selectedProgramUnitCompany.companyId,
        },
      },
      ccx_admitdate: {
        type: "datetime",
        value: new Date(data.dateOfAdmission),
      },
    };

    if (addNewClient) {
      const contactObj = {
        ccx_contacttype: {
          type: "optionset",
          value: 803080001,
        },
        firstname: {
          type: "string",
          value: data.firstName,
        },
        lastname: {
          type: "string",
          value: data.lastName,
        },
      };

      if (data.dateOfBirth && data.dateOfBirth !== "")
        contactObj.birthdate = {
          type: "datetime",
          value: new Date(data.dateOfBirth),
        };
      if (data.sex && data.sex !== "")
        contactObj.gendercode = {
          type: "optionset",
          value: parseInt(data.sex),
        };
      if (data.ssn && data.ssn !== "")
        contactObj.ccx_ssn = { type: "string", value: data.ssn };

      const toastId = toast.loading(
        `Contact ${data.lastName}, ${data.firstName} is being created...`,
        { type: toast.TYPE.INFO }
      );
      createEntity(
        "contact",
        JSON.stringify(contactObj),
        safyInfo,
        selectedProgramUnitCompany.companyName.split(" of ")[1]
      )
        .then((contactResponse) => {
          const contactResult = contactResponse.resultset.result;
          setClientId(contactResult.contactid);

          clientCompanyObj.ccx_client = {
            type: "entity",
            value: {
              entityName: "contact",
              entityId: contactResult.contactid,
            },
          };

          toast.update(toastId, {
            render:
              "Contact has been created. Client Company is being created...",
          });
          createEntity(
            "ccx_clientagency",
            JSON.stringify(clientCompanyObj),
            safyInfo,
            selectedProgramUnitCompany.companyName.split(" of ")[1]
          )
            .then((clientCompanyResponse) => {
              const clientCompanyResult =
                clientCompanyResponse.resultset.result;

              toast.update(toastId, {
                render:
                  "Client Company has been created. Admission records are being created...",
              });
              createAdmissionRecords(
                contactResult.contactid,
                clientCompanyResult.ccx_clientagencyid,
                toastId
              );
            })
            .catch(() => {
              toast.update(toastId, {
                render: "An error occurred while creating the Client Company.",
                type: toast.TYPE.ERROR,
                isLoading: false,
                closeOnClick: true,
                autoClose: 5000,
              });

              closeUpdateRecordsModal();
            });
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Contact.",
            type: toast.TYPE.ERROR,
            autoClose: 5000,
            isLoading: false,
            closeOnClick: true,
          });

          closeUpdateRecordsModal();
        });
    } else {
      const clientOption = clientOptions.filter(
        (option) => option._id === data.client
      )[0];
      if (clientOption.contactType !== "Client") {
        const client = {
          ccx_contacttype: { type: "optionset", value: 803080001 },
        };

        updateEntity("contact", data.client, JSON.stringify(client), safyInfo);
      }

      clientCompanyObj.ccx_client = {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: data.client,
        },
      };

      setClientId(data.client);

      const clientCompany = clientCompanies.filter(
        (company) =>
          getFieldText(company.ccx_company) ===
          selectedProgramUnitCompany.companyId
      )[0];

      if (!clientCompany) {
        const toastId = toast.loading("Client Company is being created...", {
          type: toast.TYPE.INFO,
        });
        createEntity(
          "ccx_clientagency",
          JSON.stringify(clientCompanyObj),
          safyInfo,
          selectedProgramUnitCompany.companyName.split(" of ")[1]
        )
          .then((clientCompanyResponse) => {
            toast.update(toastId, {
              render:
                "Client Company has been created. Admission records are being created...",
            });
            const clientCompanyResult = clientCompanyResponse.resultset.result;
            createAdmissionRecords(
              getFieldText(clientCompanyResult.ccx_client),
              clientCompanyResult.ccx_clientagencyid,
              toastId
            );
          })
          .catch(() => {
            toast.update(toastId, {
              render: "An error occurred while creating the Client Company.",
              type: toast.TYPE.ERROR,
              autoClose: 5000,
              isLoading: false,
              closeOnClick: true,
            });
          });
      } else {
        const toastId = toast.loading(
          "Admission records are being created...",
          {
            type: toast.TYPE.INFO,
          }
        );
        createAdmissionRecords(
          data.client,
          clientCompany.ccx_clientagencyid,
          toastId
        );
      }
    }
  };

  const createAdmissionRecords = (clientId, clientCompanyId, toastId) => {
    let clientProgramObj;
    let fosterHomeProgramObj;
    let diagnosisObj;
    let diagnosisDetailObj;

    clientProgramObj = {
      ccx_patient: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: clientId,
        },
      },
      ccx_company: {
        type: "entity",
        value: {
          entityName: "ccx_clientagency",
          entityId: clientCompanyId,
        },
      },
      ccx_programunit: {
        type: "entity",
        value: {
          entityName: "ccx_unit",
          entityId: data.programUnit,
        },
      },
      ccx_admitdate: {
        type: "datetime",
        value: new Date(data.dateOfAdmission),
      },
    };

    if (addFosterHome) {
      fosterHomeProgramObj = {
        ccx_patient: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: clientId,
          },
        },
        ccx_company: {
          type: "entity",
          value: {
            entityName: "ccx_clientagency",
            entityId: clientCompanyId,
          },
        },
        ccx_programunit: {
          type: "entity",
          value: {
            entityName: "ccx_unit",
            entityId: fosterHomeUnit.ccx_unitid,
          },
        },
        ccx_admitdate: {
          type: "datetime",
          value: new Date(data.dateOfAdmission),
        },
      };
    }
    if (!bedDiagnosis) {
      diagnosisObj = {
        ccx_contactid: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: clientId,
          },
        },
        ccx_name: {
          type: "string",
          value: "Bed Diagnosis",
        },
        ccx_diagnosistype: {
          type: "optionset",
          value: 803080002,
        },
        ccx_icd10: {
          type: "entity",
          value: {
            entityName: "ccx_icd10",
            entityId: "{2CFB755B-BED6-E711-A88A-005056822045}", // Bed Diagnosis
          },
        },
        ccx_code: {
          type: "string",
          value: "BD",
        },
        ccx_sourceofdata: {
          type: "optionset",
          value: 803080000,
        },
        ccx_diagnosingprovider: {
          type: "entity",
          value: {
            entityName: "systemuser",
            entityId: "{8B53EFBE-DC1E-E711-B4D4-00155D1E1FBD}", // Admin, crmadmin
          },
        },
        ccx_effdate: {
          type: "datetime",
          value: new Date(data.dateOfAdmission),
        },
      };

      diagnosisDetailObj = {
        ccx_contact: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: clientId,
          },
        },
        ccx_rank: {
          type: "number",
          value: 1,
        },
        ccx_effdate: {
          type: "datetime",
          value: new Date(data.dateOfAdmission),
        },
      };
    } else {
      diagnosisDetailObj = {
        ccx_contact: {
          type: "entity",
          value: {
            entityName: "contact",
            entityId: clientId,
          },
        },
        ccx_clientdiagnosis: {
          type: "entity",
          value: {
            entityName: "ccx_clientdiagnosis",
            entityId: getFieldText(bedDiagnosis.ccx_clientdiagnosis),
          },
        },
        ccx_rank: {
          type: "number",
          value: 1,
        },
        ccx_effdate: {
          type: "datetime",
          value: new Date(data.dateOfAdmission),
        },
      };
    }

    createEntity(
      "ccx_client_program",
      JSON.stringify(clientProgramObj),
      safyInfo,
      selectedProgramUnitCompany.companyName.split(" of ")[1]
    )
      .then((response) => {
        if (!addFosterHome)
          toast.update(toastId, {
            render: "Admission records have been created.",
            autoClose: 5000,
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            closeOnClick: true,
          });

        let clientProgramResult = response.resultset.result;
        clientProgramResult["con.contactid"] = clientId;

        if (addNewClient) {
          if (data.dateOfBirth) {
            const birthDate = moment(data.dateOfBirth);
            clientProgramResult["con.birthdate"] = {
              "#text": `${birthDate.format(moment.HTML5_FMT.DATE)}T10:59:00`,
              "@time": birthDate.format("LT"),
              "@date": birthDate.format("L"),
            };
          }
        } else {
          // clientOptions[x].dob is an object
          const selected = clientOptions.filter(
            (option) => option._id === data.client
          )[0];
          clientProgramResult["con.birthdate"] = selected.dob;
        }

        setClientProgram(clientProgramResult);
      })
      .catch(() => {
        toast.update(toastId, {
          render: "Admission records have been partially created.",
          autoClose: 5000,
          type: toast.TYPE.WARNING,
          isLoading: false,
          closeOnClick: true,
        });

        closeUpdateRecordsModal();
      });

    if (addFosterHome) {
      createEntity(
        "ccx_client_program",
        JSON.stringify(fosterHomeProgramObj),
        safyInfo,
        selectedProgramUnitCompany.companyName.split(" of ")[1]
      )
        .then((fosterHomeProgramResponse) => {
          const fosterHomeProgramResult =
            fosterHomeProgramResponse.resultset.result;
          diagnosisDetailObj.ccx_clientprogram = {
            type: "entity",
            value: {
              entityName: "ccx_client_program",
              entityId: fosterHomeProgramResult.ccx_client_programid,
            },
          };
          setFosterHomeProgram(fosterHomeProgramResult);

          if (!bedDiagnosis) {
            createEntity(
              "ccx_clientdiagnosis",
              JSON.stringify(diagnosisObj),
              safyInfo,
              selectedProgramUnitCompany.companyName.split(" of ")[1]
            )
              .then((diagnosisResponse) => {
                console.log("madeit");
                const diagnosisResult = diagnosisResponse.resultset.result;

                diagnosisDetailObj.ccx_clientdiagnosis = {
                  type: "entity",
                  value: {
                    entityName: "ccx_clientdiagnosis",
                    entityId: diagnosisResult.ccx_clientdiagnosisid,
                  },
                };

                createEntity(
                  "ccx_clientdiagnosisdetail",
                  JSON.stringify(diagnosisDetailObj),
                  safyInfo,
                  selectedProgramUnitCompany.companyName.split(" of ")[1]
                )
                  .then(() => {
                    toast.update(toastId, {
                      render: "Admission records have been created.",
                      autoClose: 5000,
                      type: toast.TYPE.SUCCESS,
                      isLoading: false,
                      closeOnClick: true,
                    });
                  })
                  .catch(() => {
                    toast.update(toastId, {
                      render: "Admission records have been partially created.",
                      autoClose: 5000,
                      type: toast.TYPE.WARNING,
                      isLoading: false,
                      closeOnClick: true,
                    });

                    closeUpdateRecordsModal();
                  });
              })
              .catch(() => {
                toast.update(toastId, {
                  render: "Admission records have been partially created.",
                  autoClose: 5000,
                  type: toast.TYPE.WARNING,
                  isLoading: false,
                  closeOnClick: true,
                });

                closeUpdateRecordsModal();
              });
          } else {
            createEntity(
              "ccx_clientdiagnosisdetail",
              JSON.stringify(diagnosisDetailObj),
              safyInfo,
              selectedProgramUnitCompany.companyName.split(" of ")[1]
            )
              .then(() => {
                toast.update(toastId, {
                  render: "Admission records have been created.",
                  autoClose: 5000,
                  type: toast.TYPE.SUCCESS,
                  isLoading: false,
                  closeOnClick: true,
                });
              })
              .catch(() => {
                toast.update(toastId, {
                  render: "Admission records have been partially created.",
                  autoClose: 5000,
                  type: toast.TYPE.WARNING,
                  isLoading: false,
                  closeOnClick: true,
                });

                closeUpdateRecordsModal();
              });
          }
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the admission records.",
            autoClose: 5000,
            type: toast.TYPE.ERROR,
            isLoading: false,
            closeOnClick: true,
          });

          closeUpdateRecordsModal();
        });
    }
  };

  const handleKeyDown = (e) => {
    if (checkKeyPress(e)) {
      handleClientSearch();
    }
  };

  const handleAddNewClient = (value) => {
    setAddNewClient(value);
    setData((d) => {
      const obj = { ...d };
      obj.addNewClient = value;

      if (!value) {
        obj.firstName = "";
        obj.lastName = "";
        obj.dateOfBirth = "";
        obj.sex = "";
        obj.ssn = "";
      }
      return obj;
    });

    setErrors((e) => {
      const errs = { ...e };
      delete errs.firstName;
      delete errs.lastName;
      delete errs.sex;
      delete errs.dateOfBirth;
      delete errs.client;
      return errs;
    });
  };

  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Client, Company, Program Admission</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="container">
              <div className="row">
                <div className="col py-2">
                  <label htmlFor="clientSearchQuery" className="form-label">
                    Search for Client
                  </label>
                  <input
                    name="clientSearchQuery"
                    id={clientSearchQuery}
                    placeholder="Search for Client..."
                    value={clientSearchQuery}
                    className="form-control"
                    onChange={(e) =>
                      setClientSearchQuery(e.currentTarget.value)
                    }
                    disabled={addNewClient}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="col-3 py-2">
                  <button
                    className="btn btn-primary"
                    style={{ marginTop: "30px" }}
                    onClick={handleClientSearch}
                    disabled={clientSearchQuery === ""}
                  >
                    Search
                  </button>
                </div>
              </div>
              <div className="row">
                <div className="col py-2">
                  <Select
                    name="client"
                    label="Clients"
                    options={clientOptions}
                    onChange={handleClientChange}
                    error={errors.client}
                    disabled={!loadStatus.contactsLoaded || addNewClient}
                    placeholder={
                      loadStatus.loadingContacts ? "Searching..." : ""
                    }
                  />
                </div>
                <div className="col-3 py-2">
                  {!addNewClient ? (
                    <button
                      className={
                        data.client ? "btn btn-secondary" : "btn btn-primary"
                      }
                      style={{ marginTop: "30px" }}
                      onClick={() => handleAddNewClient(true)}
                      disabled={!loadStatus.contactsLoaded || data.client}
                    >
                      New
                    </button>
                  ) : null}
                  {addNewClient ? (
                    <button
                      className="btn btn-danger"
                      style={{ marginTop: "30px" }}
                      onClick={() => handleAddNewClient(false)}
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </div>
              {addNewClient ? (
                <>
                  <div className="row">
                    <div className="col py-2">
                      <Input
                        name="firstName"
                        label="First Name"
                        onChange={handleChange}
                        error={errors.firstName}
                      />
                    </div>
                    <div className="col py-2">
                      <Input
                        name="lastName"
                        label="Last Name"
                        onChange={handleChange}
                        error={errors.lastName}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2">
                      <Input
                        name="dateOfBirth"
                        label="Date of Birth"
                        type="date"
                        onChange={handleChange}
                        error={errors.dateOfBirth}
                      />
                    </div>
                    <div className="col py-2">
                      <Select
                        name="sex"
                        label="Sex"
                        options={contactOptionSets.gendercode}
                        onChange={handleChange}
                        error={errors.sex}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col py-2">
                      <Input
                        name="ssn"
                        label="SSN"
                        value={data.ssn}
                        onChange={handleSSNChange}
                        error={errors.ssn}
                        maxlength="11"
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                </>
              ) : null}
              {data.client || (data.firstName && data.lastName) ? (
                <>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="dateOfAdmission"
                        label="Date of Admission"
                        type="date"
                        value={data.dateOfAdmission}
                        onChange={handleChange}
                        error={errors.dateOfAdmission}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Select
                        name="programUnit"
                        label="Program Unit"
                        options={programUnitOptions}
                        value={data.programUnit}
                        onChange={handleProgramUnitChange}
                        error={errors.programUnit}
                        disabled={programUnitOptions.length === 0}
                      />
                    </div>
                  </div>
                </>
              ) : null}
              {(data.client || (data.firstName && data.lastName)) &&
              data.programUnit !== undefined &&
              data.programUnit !== "" ? (
                <div className="row">
                  <div className="col">
                    <ul>
                      {addNewClient && (
                        <>
                          <li>{`Client - ${data.firstName} ${
                            data.lastName
                          } ${getGender()} ${
                            data.dateOfBirth
                              ? "DOB: " +
                                moment
                                  .utc(new Date(data.dateOfBirth))
                                  .format("MM-DD-YYYY")
                              : ""
                          }`}</li>
                          <li>{`Client Company - ${
                            selectedProgramUnitCompany &&
                            selectedProgramUnitCompany.companyName
                          }`}</li>
                        </>
                      )}
                      {!addNewClient &&
                        clientCompanies.filter(
                          (company) =>
                            getFieldText(company.ccx_company) ===
                            selectedProgramUnitCompany.companyId
                        ).length === 0 && (
                          <li>{`Client Company - ${
                            selectedProgramUnitCompany &&
                            selectedProgramUnitCompany.companyName
                          }`}</li>
                        )}
                      <li>{`Client Program - ${getProgramUnitName()} - ${
                        data.dateOfAdmission &&
                        moment
                          .utc(new Date(data.dateOfAdmission))
                          .format("MM-DD-YYYY")
                      }`}</li>
                      {addFosterHome && (
                        <>
                          <li>{`Client Program - ${fosterHomeUnit.ccx_name} - ${
                            data.dateOfAdmission &&
                            moment
                              .utc(new Date(data.dateOfAdmission))
                              .format("MM-DD-YYYY")
                          }`}</li>
                          {!bedDiagnosis && (
                            <li>{`Diagnosis - Bed Diagnosis - ${
                              data.dateOfAdmission &&
                              moment
                                .utc(new Date(data.dateOfAdmission))
                                .format("MM-DD-YYYY")
                            }`}</li>
                          )}
                          <li>{`Diagnosis Detail - Bed Diagnosis - ${
                            fosterHomeUnit.ccx_name
                          } - ${
                            data.dateOfAdmission &&
                            moment
                              .utc(new Date(data.dateOfAdmission))
                              .format("MM-DD-YYYY")
                          }`}</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              ) : null}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-primary"
              onClick={() => {
                nextModal && nextModal(true);
              }}
            >
              Skip
            </button>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary"
                  : "btn btn-secondary"
              }
              disabled={Object.keys(errors).length !== 0}
              onClick={() => {
                if (!validate()) {
                  handleSave();
                  nextModal && nextModal();
                }
              }}
            >
              Submit and {isLastModal ? "End" : "Next"}
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default ClientIntakeModal;
